import { useEffect, useSyncExternalStore } from 'react';
import { ApiError, callsApi, type DeskInPatch, type PhoneCallView } from '../api/client';
import { useMessagesLive } from './messages';

/**
 * Calls on the person's own phones: their desk phone (or the phone system's own app), not calls in
 * Stitch. They show in the call dock with Hold and Hang up, as the person's own portal on the phone
 * system does it. Read when their line changes (the phone system's live connection says so at
 * once), every few seconds while a call is up (on hold, or ended on the desk phone), and when
 * Stitch comes back into view or reconnects.
 */
export interface PhoneCallsState {
  tenant: string;
  calls: PhoneCallView[];
  /** When the calls were read (Date.now()), so each call's clock counts on from its `seconds`. */
  readAt: number;
  /** Stitch may hold or hang them up at this company. */
  control: boolean;
  /** Their desk phone in a Patch (Live sessions): shown as part of the Patch, not as a call. */
  patches: DeskInPatch[];
  /** "hold:<id>" or "hangup:<id>" while it's being sent. */
  busy: string | null;
  problem: string | null;
}

const START: PhoneCallsState = { tenant: '', calls: [], readAt: 0, control: false, patches: [], busy: null, problem: null };
let state: PhoneCallsState = START;
const listeners = new Set<() => void>();

function set(next: Partial<PhoneCallsState>): void {
  state = { ...state, ...next };
  for (const l of [...listeners]) l();
}

export function usePhoneCalls(): PhoneCallsState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => state
  );
}

/** While a call is up: read again this often (a hold, or the call ending on the desk phone). */
const WHILE_ON_MS = 4000;
let timer: number | undefined;
let loading = false;
let again = false;

async function load(tenant: string): Promise<void> {
  if (loading) {
    again = true;
    return;
  }
  loading = true;
  try {
    const r = await callsApi.phoneCalls(tenant);
    if (state.tenant === tenant) set({ calls: r.calls, control: r.control, patches: r.patches ?? [], readAt: Date.now() });
  } catch (err) {
    // No extension here, or the phone system can't be asked right now: nothing to show (a call
    // already shown stays until the next read says otherwise).
    if (state.tenant === tenant && err instanceof ApiError && (err.status === 403 || err.status === 404)) set({ calls: [], patches: [] });
  } finally {
    loading = false;
    if (again) {
      again = false;
      void load(tenant);
    } else schedule(tenant);
  }
}

function schedule(tenant: string): void {
  window.clearTimeout(timer);
  timer = undefined;
  if (state.tenant !== tenant || (!state.calls.length && !state.patches.length)) return;
  timer = window.setTimeout(() => (document.visibilityState === 'visible' ? void load(tenant) : schedule(tenant)), WHILE_ON_MS);
}

/**
 * Keeps the calls on this person's phones at a company in step. `extension`: theirs there (none,
 * nothing to show).
 */
export function usePhoneCallsLive(tenant: string, extension: string | undefined, on: boolean): void {
  const t = tenant.toLowerCase();
  const live = on && Boolean(t && extension);
  useEffect(() => {
    if (!live) {
      if (state.tenant === t && (state.calls.length || state.patches.length)) set({ calls: [], patches: [] });
      return;
    }
    if (state.tenant !== t) set({ ...START, tenant: t });
    void load(t);
    const back = () => {
      if (document.visibilityState === 'visible') void load(t);
    };
    document.addEventListener('visibilitychange', back);
    return () => {
      document.removeEventListener('visibilitychange', back);
      window.clearTimeout(timer);
      timer = undefined;
    };
  }, [t, live]);
  useMessagesLive((e) => {
    if (e.type === 'reset' || e.type === 'hello') {
      void load(t);
      return;
    }
    // Their line changed (a call rang, was answered or ended, on any of their phones).
    if (e.type === 'line' && e.tenant === t && e.extension === extension) void load(t);
  }, live);
}

/**
 * Hold one of the calls on their phones, take it off hold, transfer it (to an extension or a
 * number) or hang it up. Resolves true when it was done.
 */
export async function controlPhoneCall(id: string, action: 'hold' | 'resume' | 'hangup' | 'transfer', to?: string): Promise<boolean> {
  const t = state.tenant;
  if (!t || state.busy) return false;
  set({ busy: `${action}:${id}`, problem: null });
  try {
    const r = action === 'transfer' ? await callsApi.transferPhoneCall(t, id, to ?? '') : await callsApi.phoneCall(t, id, action);
    if (state.tenant === t) set({ calls: r.calls, readAt: Date.now() });
    return true;
  } catch (err) {
    if (err instanceof ApiError && err.code === 'CALL_GONE') set({ calls: state.calls.filter((c) => c.id !== id) });
    else set({ problem: err instanceof ApiError ? err.message : 'That didn’t work. Try again.' });
    return false;
  } finally {
    set({ busy: null });
    schedule(t);
  }
}

export function dismissPhoneCallProblem(): void {
  set({ problem: null });
}
